import React, { useState } from "react";
import classnames from "classnames";
import { Button, Col, FormFeedback, Input, InputGroup, Label, Row } from "reactstrap";
import ModelMediaContainer from "../Media/ModelMediaContainer";

/**
 * categorymenu building block (SCRUM-642, VariableField registry) — an
 * `items` array of {logo picked via the media modal, url derived from a
 * published page's page_configuration.path via a page-type → page cascade,
 * description textarea}. Emits the FULL canonical value via
 * `onChange(settingVar.variable, { items: [...] })` on every edit; all four
 * item keys (`logo:{type:'media',url}`, `url`, `description`,
 * `selectedPageType`) are always present with '' defaults.
 *
 * Pinned sources (byte-identity):
 *  - wizard IndexTemplateSelector.jsx categorymenu IIFE (SCRUM-630
 *    tests/integration/pages/contentVariables.types.test.jsx)
 *  - the former Required host (.jsx) / the former Optional host (.jsx) branches (SCRUM-640
 *    handleContentRequired.composites.test.jsx /
 *    handleContentOptional.types.test.jsx)
 *  - e2e variableBaselineComposites.spec.ts + pages/createPages.ts
 *    fillWizardCategorymenu (SCRUM-746 stored-at-publish shape)
 *
 * Host envelope mapping (SCRUM-643 concern, NOT implemented here): the wizard
 * wraps the emit as `onChangeVariableValue({ target: { name, value } })`
 * (host mutates the Content entry, then setContent → onContentChange(Content));
 * the update surfaces wrap it as setVariables +
 * `onVariableChange(wholeContentVarObject)`.
 *
 * Variant differences (variant="create" = wizard, "update" = both update
 * surfaces, which are character-identical):
 *  - <details>: create `className="bottomblockright-container"` +
 *    `data-collapsible`, collapsed (no `open`); update
 *    `className="categorymenu-container mb-3" open` (key left to the parent).
 *  - item wrapper: create `bbr-item mb-3` (no inline style); update
 *    `categorymenu-item mb-4 p-3` + grey card inline style.
 *  - remove (−) button: create never disabled (can reach `items: []`); update
 *    disabled at one item.
 *  - add button label: create "+ Add item"; update "+ Add category menu item".
 *  - row spacing: update adds `mt-2` on the URL and Description rows (the
 *    Selected Path row has `mt-2` in both).
 *  - validation chrome (create only): textarea `is-invalid` class when
 *    `validationError` is set; trailing FormFeedback row when
 *    `isValidationAttempted && validationError`.
 *
 * Preserved quirks:
 *  - `initial` branch order: any truthy object value (INCLUDING arrays and
 *    objects without an `items` array) takes branch 1 → `items: []` renders
 *    zero rows; the Array.isArray branch is dead code, kept verbatim. Only
 *    falsy values ('' template seed, null, undefined) seed one blank item.
 *  - value is host-owned and recomputed from settingVar.value every render.
 *  - normalizeItems tolerates legacy string logos and re-stamps
 *    `type:'media'`; first edit materializes the full object ('' defaults).
 *  - updatePageType resets `url` to '' (hides the Selected Path chip);
 *    logo/description are preserved.
 *  - only the path is stored; the page select's controlled value is a reverse
 *    lookup by path across ALL published pages (first match wins; an url
 *    matching no page shows the placeholder while the chip still renders).
 *  - published = `publish_statement !== false` (missing field counts as
 *    published); pageTypes keep first-seen order, de-duped, falsy dropped.
 *  - setItems always goes through `setValue({ ...value, items })` (spread kept
 *    so future keys on the object would survive).
 *  - media picker results without `secure_url` are silently ignored; there is
 *    no way to remove a logo once picked, only replace it.
 *  - remove-to-empty in create is unguarded (`items: []` emits, Add restores
 *    rows).
 *  - rows are keyed on a client-only id (`rowKeys`), never stored: each row's
 *    media picker keeps an unconfirmed pick in local state, and `key={i}`
 *    handed it to the next row when a row was removed (SCRUM-2183).
 * Dropped (allowed cleanup): the wizard summary's redundant second
 * `dragHandleProps` spread — the `summaryProps` passthrough replaces it once,
 * hosts decide what to pass.
 */
let lastRowKey = 0;
const nextRowKey = () => ++lastRowKey;

export type CategoryMenuPage = {
  _id?: string;
  global_title?: string;
  publish_statement?: boolean;
  page_configuration?: { type?: string; path?: string };
};

export type CategoryMenuFieldProps = {
  /** the stored page_content value is untyped JSON; this block normalises it below */
  settingVar: { variable?: string; value?: any }; // any: stored page_content value; legacy shapes include string logos and objects without items, normalized below
  /** reports the canonical value object for the variable */
  onChange: (name: string | undefined, value: Record<string, unknown>) => void;
  variant?: "create" | "update";
  summaryProps?: Record<string, unknown>;
  validationError?: React.ReactNode;
  isValidationAttempted?: boolean;
  /** the site's pages the url picker offers */
  pages?: CategoryMenuPage[];
};

export default function CategoryMenuField({
  settingVar,
  onChange,
  variant = "update",
  pages = [],
  summaryProps = {},
  validationError,
  isValidationAttempted = false,
}: CategoryMenuFieldProps) {
  const isCreate = variant === "create";
  const fieldName = settingVar.variable;

  // Get published pages and extract unique page types
  const publishedPages = (pages || []).filter((p) => p.publish_statement !== false);
  const pageTypes = [
    // same truthiness filter as before, written as a guard so TS keeps the narrowing
    ...new Set(publishedPages.map((p) => p.page_configuration?.type).filter((t): t is string => Boolean(t))),
  ];

  // Normalize incoming value to: { items: [{ logo:{...}, url:'', description:'', selectedPageType:'' }] }
  const normalizeItems = (arr) =>
    (Array.isArray(arr) ? arr : []).map((it) => ({
      logo:
        it && typeof it.logo === "object"
          ? { type: "media", url: it.logo.url || "" }
          : { type: "media", url: (it && it.logo) || "" },
      url: (it && it.url) || "",
      description: (it && it.description) || "",
      selectedPageType: (it && it.selectedPageType) || "",
    }));

  const initial =
    settingVar.value && typeof settingVar.value === "object"
      ? { items: normalizeItems(settingVar.value.items) }
      : Array.isArray(settingVar.value)
      ? { items: normalizeItems(settingVar.value) }
      : { items: [{ logo: { type: "media", url: "" }, url: "", description: "", selectedPageType: "" }] };

  const value = initial;

  // One client-only id per row, kept beside the value (the stored items carry no id).
  // removeItem drops the removed row's id; any other change in length (add, a reload
  // from the host) is matched here, by appending or trimming at the end.
  const [rowKeys, setRowKeys] = useState<number[]>(() => value.items.map(nextRowKey));
  if (rowKeys.length !== value.items.length) {
    const n = value.items.length;
    setRowKeys(n > rowKeys.length ? [...rowKeys, ...Array.from({ length: n - rowKeys.length }, nextRowKey)] : rowKeys.slice(0, n));
  }

  const setValue = (next) => onChange(fieldName, next);

  const setItems = (nextItems) => setValue({ ...value, items: nextItems });

  const updateDesc = (idx) => (e) => {
    const items = value.items.map((it, i) =>
      i === idx ? { ...it, description: e.target.value } : it
    );
    setItems(items);
  };

  // Handler for page type dropdown
  const updatePageType = (idx) => (e) => {
    const items = value.items.map((it, i) =>
      i === idx ? { ...it, selectedPageType: e.target.value, url: "" } : it
    );
    setItems(items);
  };

  // Handler for page selection dropdown - saves page_configuration.path as url
  const updatePageSelection = (idx) => (e) => {
    const selectedPageId = e.target.value;
    const selectedPage = publishedPages.find((p) => p._id === selectedPageId);
    const pagePath = selectedPage?.page_configuration?.path || "";
    const items = value.items.map((it, i) => (i === idx ? { ...it, url: pagePath } : it));
    setItems(items);
  };

  // Get pages filtered by selected type for a specific item
  const getFilteredPages = (selectedType) => {
    if (!selectedType) return [];
    return publishedPages.filter((p) => p.page_configuration?.type === selectedType);
  };

  const onMediaLogoChange = (idx) => (img) => {
    if (!img?.secure_url) return;
    const items = value.items.map((it, i) =>
      i === idx ? { ...it, logo: { type: "media", url: img.secure_url } } : it
    );
    setItems(items);
  };

  const addItem = () =>
    setItems([
      ...value.items,
      { logo: { type: "media", url: "" }, url: "", description: "", selectedPageType: "" },
    ]);

  const removeItem = (idx) => {
    setRowKeys((keys) => keys.filter((_, i) => i !== idx));
    setItems(value.items.filter((_, i) => i !== idx));
  };

  const logoSrc = (logo) => logo?.url || "";

  return (
    <details
      className={isCreate ? "bottomblockright-container" : "categorymenu-container mb-3"}
      data-collapsible={isCreate || undefined}
      open={!isCreate}
    >
      <summary
        className="varible_container form-control-label d-flex align-items-center"
        {...summaryProps}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {fieldName}
      </summary>

      <div className="pt-3">
        {value.items.map((item, i) => (
          <div
            key={rowKeys[i] ?? `new-${i}`}
            className={isCreate ? "bbr-item mb-3" : "categorymenu-item mb-4 p-3"}
            {...(isCreate
              ? {}
              : {
                  style: {
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                  },
                })}
          >
            {/* Logo row */}
            <Row className="varible_container align-items-center">
              <Label md="2" className="form-control-label">Logo #{i + 1}</Label>

              <Col md="6">
                <ModelMediaContainer selectedImg={onMediaLogoChange(i)} />
              </Col>

              <Col md="3">
                {logoSrc(item.logo) ? (
                  <img
                    src={logoSrc(item.logo)}
                    alt={`logo-${i}`}
                    style={{ maxWidth: "100%", maxHeight: 60, objectFit: "contain", borderRadius: 6 }}
                  />
                ) : (
                  <span style={{ fontSize: 12, color: "#888" }}>No logo yet</span>
                )}
              </Col>

              <Col md="1" className="d-flex justify-content-end">
                <Button
                  type="button"
                  color="danger"
                  size="sm"
                  onClick={() => removeItem(i)}
                  disabled={!isCreate && value.items.length === 1}
                >
                  &minus;
                </Button>
              </Col>
            </Row>

            {/* URL row - Page Type and Page Selection dropdowns */}
            <Row className={isCreate ? "varible_container" : "varible_container mt-2"}>
              <Label md="2" className="form-control-label">URL #{i + 1}</Label>
              <Col md="10">
                <Row>
                  <Col md="6" className="pr-1">
                    <Input
                      type="select"
                      value={item.selectedPageType || ""}
                      onChange={updatePageType(i)}
                      name={`${fieldName}[${i}].pageType`}
                    >
                      <option value="">-- Select Page Type --</option>
                      {pageTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md="6" className="pl-1">
                    <Input
                      type="select"
                      value={publishedPages.find((p) => p.page_configuration?.path === item.url)?._id || ""}
                      onChange={updatePageSelection(i)}
                      name={`${fieldName}[${i}].page`}
                      disabled={!item.selectedPageType}
                    >
                      <option value="">-- Select Page --</option>
                      {getFilteredPages(item.selectedPageType).map((page) => (
                        <option key={page._id} value={page._id}>
                          {page.global_title || page.page_configuration?.path}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Selected Path display */}
            {item.url && (
              <Row className="varible_container mt-2">
                <Label md="2" className="form-control-label">Selected Path</Label>
                <Col md="10">
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: "#e8f5e9",
                      border: "1px solid #4caf50",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      fontSize: "14px",
                    }}
                  >
                    <i className="fas fa-link" style={{ color: "#4caf50", marginRight: "10px", fontSize: "14px" }} />
                    <span style={{ color: "#2e7d32", fontWeight: "600" }}>{item.url}</span>
                  </div>
                </Col>
              </Row>
            )}

            {/* Description row */}
            <Row className={isCreate ? "varible_container" : "varible_container mt-2"}>
              <Label md="2" className="form-control-label">Description #{i + 1}</Label>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>
                  <textarea
                    rows={4}
                    cols={70}
                    placeholder="Type the description…"
                    value={item.description}
                    onChange={updateDesc(i)}
                    name={`${fieldName}[${i}].description`}
                    {...(isCreate ? { className: validationError ? "is-invalid" : "" } : {})}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>
        ))}

        <Row className="mt-3">
          <Col md={{ offset: 2, size: 10 }}>
            <Button type="button" color="primary" onClick={addItem}>
              {isCreate ? "+ Add item" : "+ Add category menu item"}
            </Button>
          </Col>
        </Row>

        {isCreate && isValidationAttempted && validationError && (
          <Row className="mt-2">
            <Col md={{ offset: 2, size: 10 }}>
              <FormFeedback className="medium-text d-block">
                {validationError}
              </FormFeedback>
            </Col>
          </Row>
        )}
      </div>
    </details>
  );
}

// renders its own <summary>/header chrome - the dispatcher skips its label row (SCRUM-643)
CategoryMenuField.ownsChrome = true;
